import { type ReactNode } from 'react';

import { Grid } from '@eneco/sparky';
import {
  type Control,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';

import { PersonalDetailsBirthdate, PersonalDetailsSalutation, PersonalDetailsTextField } from './PersonalDetailsFields';

type TextFieldContent = {
  label?: string;
  placeholder?: string;
  hint?: string;
};

export interface PersonalDetailsContent {
  salutationRadio?: { value?: { label?: string; options?: { name: string; label: string }[] } };
  firstNameFormField?: { value?: TextFieldContent };
  surnamePrepositionFormField?: { value?: TextFieldContent };
  surnameFormField?: { value?: TextFieldContent };
  birthdateFormField?: { value?: TextFieldContent };
  dayFormField?: { value?: TextFieldContent };
  monthFormField?: { value?: TextFieldContent };
  yearFormField?: { value?: TextFieldContent };
}

type PersonalDetailsFormValues = FieldValues & {
  salutation: string;
  firstName: string;
  surnamePreposition: string;
  surname: string;
  birthdate?: string;
  day: string | number;
  month: string | number;
  year: string | number;
};

const fieldError = (error?: { message?: unknown }) => (typeof error?.message === 'string' ? error.message : undefined);

export const PersonalDetailsFormFields = <TFieldValues extends PersonalDetailsFormValues>({
  control,
  register,
  setValue,
  errors,
  personalDetails,
  initialsField,
}: {
  control: Control<TFieldValues>;
  register: UseFormRegister<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  personalDetails?: PersonalDetailsContent;
  initialsField?: ReactNode;
}) => (
  <>
    <PersonalDetailsSalutation
      control={control}
      error={fieldError(errors.salutation)}
      field={personalDetails?.salutationRadio?.value}
    />
    <PersonalDetailsTextField
      name={'firstName' as FieldPath<TFieldValues>}
      register={register}
      setValue={setValue}
      transform="capitalize"
      field={personalDetails?.firstNameFormField?.value}
      error={fieldError(errors.firstName)}
    />
    <Grid columns="2" columnGap="3">
      {initialsField}
      <PersonalDetailsTextField
        name={'surnamePreposition' as FieldPath<TFieldValues>}
        register={register}
        setValue={setValue}
        field={personalDetails?.surnamePrepositionFormField?.value}
        error={fieldError(errors.surnamePreposition)}
      />
    </Grid>
    <PersonalDetailsTextField
      name={'surname' as FieldPath<TFieldValues>}
      register={register}
      setValue={setValue}
      transform="capitalize"
      field={personalDetails?.surnameFormField?.value}
      error={fieldError(errors.surname)}
    />
    <PersonalDetailsBirthdate
      register={register}
      setValue={setValue}
      error={
        fieldError(errors.birthdate) ?? fieldError(errors.year) ?? fieldError(errors.month) ?? fieldError(errors.day)
      }
      hasDayError={!!fieldError(errors.day)}
      hasMonthError={!!fieldError(errors.month)}
      hasYearError={!!fieldError(errors.year)}
      birthdate={personalDetails?.birthdateFormField?.value}
      day={personalDetails?.dayFormField?.value}
      month={personalDetails?.monthFormField?.value}
      year={personalDetails?.yearFormField?.value}
    />
  </>
);
